'use client'

import * as React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel, Autoplay } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { useMediaQuery } from 'react-responsive'
import { cn } from '../../helpers/index.js'
import Picture from '../../components/picture.js'
import type { Media, Theme } from '../../types/props.js'
import { Heading, Text } from '../../components/index.js'
import { withLayout } from '../../shared/Styles.js'

export type MediaSceneSwitcherV2SemanticName =
  | 'root'
  | 'header'
  | 'title'
  | 'subtitle'
  | 'container'
  | 'tabList'
  | 'tabItem'
  | 'tabTitle'
  | 'tabDescription'
  | 'mediaWrapper'
  | 'media'
  | 'swiper'
  | 'slide'
  | 'slideMedia'
  | 'slideContent'

/**
 * 场景项数据接口
 */
export interface MediaSceneSwitcherV2Item {
  /** 场景标题 */
  title: string
  /** 场景描述 */
  description?: string
  /** 场景媒体（图片/视频） */
  media: {
    mobile?: Media
    tablet?: Media
    laptop?: Media
    desktop?: Media
    lgDesktop?: Media
  }
}

/**
 * 布局模式
 * - left: 左文右图（laptop+ 默认）
 * - right: 左图右文
 * - top: 上图下文（swiper 卡片模式）
 */
export type MediaSceneSwitcherV2Layout = 'left' | 'right' | 'top'

/**
 * MediaSceneSwitcherV2 业务组件数据接口
 */
export interface MediaSceneSwitcherV2Data {
  /** 主题 */
  theme?: Theme
  /** 主标题 */
  title?: string
  /** 副标题 */
  subtitle?: string
  /** 场景列表 */
  items: MediaSceneSwitcherV2Item[]
  /** 默认选中的场景索引 */
  defaultActiveIndex?: number
  /** 布局模式，默认 text-left */
  layout?: MediaSceneSwitcherV2Layout
  /** 是否开启自动轮播，默认 true */
  autoplay?: boolean
  /** 自动轮播间隔时间（毫秒），默认 3000 */
  autoplayDelay?: number
}

export interface MediaSceneSwitcherV2Props extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: MediaSceneSwitcherV2Data
  /** 语义化类名覆盖 */
  classNames?: Partial<Record<MediaSceneSwitcherV2SemanticName, string>>
  /** 场景切换回调 */
  onSceneChange?: (index: number, scene: MediaSceneSwitcherV2Item) => void
}

/**
 * 响应式媒体类型
 */
type ResponsiveMedia = MediaSceneSwitcherV2Item['media']

/** 断点配置 */
const BREAKPOINTS: { key: keyof ResponsiveMedia; width: number }[] = [
  { key: 'lgDesktop', width: 1920 },
  { key: 'desktop', width: 1440 },
  { key: 'laptop', width: 1024 },
  { key: 'tablet', width: 768 },
  { key: 'mobile', width: 390 },
]

/**
 * 检查当前断点的媒体是否为视频
 */
const isCurrentMediaVideo = (media: ResponsiveMedia, currentBreakpoint: keyof ResponsiveMedia): boolean => {
  return media[currentBreakpoint]?.mimeType === 'video/mp4'
}

/**
 * 将响应式媒体对象转为 Picture source 格式字符串
 * 格式: "url1 1920, url2 1440, url3 1024"
 */
const getImageSource = (media: ResponsiveMedia): string => {
  const sources = BREAKPOINTS.filter(({ key }) => media[key]?.url && media[key]?.mimeType !== 'video/mp4').map(
    ({ key, width }) => `${media[key]!.url} ${width}`
  )

  return sources.join(', ')
}

/**
 * 获取 alt 文本
 */
const getMediaAlt = (media: ResponsiveMedia, fallback: string): string => {
  for (const { key } of BREAKPOINTS) {
    if (media[key]?.alt) return media[key]!.alt
  }
  return fallback
}

/**
 * 媒体渲染组件 - 支持图片和视频，根据断点渲染对应资源
 */
const MediaRenderer: React.FC<{
  media: ResponsiveMedia
  alt: string
  className?: string
}> = ({ media, alt, className }) => {
  const isLgDesktop = useMediaQuery({ minWidth: 1920 })
  const isDesktop = useMediaQuery({ minWidth: 1440, maxWidth: 1919 })
  const isLaptop = useMediaQuery({ minWidth: 1025, maxWidth: 1439 })
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 })

  // 获取当前断点 key
  const currentBreakpoint = React.useMemo((): keyof ResponsiveMedia => {
    if (isLgDesktop && media.lgDesktop?.url) return 'lgDesktop'
    if (isDesktop && media.desktop?.url) return 'desktop'
    if (isLaptop && media.laptop?.url) return 'laptop'
    if (isTablet && media.tablet?.url) return 'tablet'
    if (media.mobile?.url) return 'mobile'

    // fallback: 返回第一个有效的断点
    for (const { key } of BREAKPOINTS) {
      if (media[key]?.url) return key
    }
    return 'desktop'
  }, [isLgDesktop, isDesktop, isLaptop, isTablet, media])

  const currentMedia = media[currentBreakpoint]

  if (!currentMedia?.url) return null

  // 当前断点是视频则渲染视频
  if (isCurrentMediaVideo(media, currentBreakpoint)) {
    return <video src={currentMedia.url} className={className} autoPlay loop muted playsInline />
  }

  // 图片：使用 Picture 组件的响应式格式
  const imageSource = getImageSource(media)

  return (
    <Picture
      source={imageSource || currentMedia.url}
      alt={getMediaAlt(media, alt)}
      className={className}
      imgClassName="size-full object-cover"
    />
  )
}

/**
 * MediaSceneSwitcherV2 - 多场景切换组件
 *
 * @description 用于展示多个场景的切换组件，支持响应式布局：
 * - mobile/tablet: 横向滑动卡片布局
 * - laptop+: 左文右图 / 左图右文布局
 */
const DEFAULT_AUTOPLAY_DELAY = 3000

const MediaSceneSwitcherV2 = React.forwardRef<HTMLDivElement, MediaSceneSwitcherV2Props>(
  ({ className, classNames = {}, data, onSceneChange, ...props }, ref) => {
    const {
      title,
      subtitle,
      items: scenes,
      defaultActiveIndex = 0,
      layout = 'left',
      autoplay = true,
      autoplayDelay = DEFAULT_AUTOPLAY_DELAY,
    } = data
    const [activeIndex, setActiveIndex] = React.useState(defaultActiveIndex)
    const swiperRef = React.useRef<SwiperType | null>(null)
    const intervalRef = React.useRef<number>(0)
    const tabRefs = React.useRef<(HTMLDivElement | null)[]>([])

    // 判断是否是桌面端
    const isLaptopOrLarger = useMediaQuery({ minWidth: 1025 })

    // 桌面端自动轮播（只在 laptop+ 生效，手机端由 Swiper 的 onSlideChange 同步）
    React.useEffect(() => {
      if (!autoplay || scenes.length <= 1 || !isLaptopOrLarger) return

      const startAutoplay = () => {
        intervalRef.current = window.setInterval(() => {
          setActiveIndex(prev => (prev + 1) % scenes.length)
        }, autoplayDelay)
      }

      startAutoplay()

      return () => {
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current)
        }
      }
    }, [autoplay, autoplayDelay, scenes.length, isLaptopOrLarger])

    // 当 activeIndex 变化时自动滚动到对应的 tab（仅当父容器可滚动时）
    React.useEffect(() => {
      if (!isLaptopOrLarger || !tabRefs.current[activeIndex]) return

      const tabElement = tabRefs.current[activeIndex]
      const parentContainer = tabElement?.parentElement

      // 检查父容器是否可滚动
      if (!parentContainer || parentContainer.scrollHeight <= parentContainer.clientHeight) return

      // 使用 requestAnimationFrame 避免阻塞主线程
      const rafId = requestAnimationFrame(() => {
        const containerRect = parentContainer.getBoundingClientRect()
        const tabRect = tabElement.getBoundingClientRect()

        // 检查元素是否已在可视区域内
        const isVisible = tabRect.top >= containerRect.top && tabRect.bottom <= containerRect.bottom

        // 如果已经可见，不需要滚动
        if (isVisible) return

        // 计算滚动距离，让 tab 尽量居中显示
        const scrollOffset = tabRect.top - containerRect.top - parentContainer.clientHeight / 2 + tabRect.height / 2

        parentContainer.scrollTo({
          top: parentContainer.scrollTop + scrollOffset,
          behavior: 'smooth',
        })
      })

      return () => cancelAnimationFrame(rafId)
    }, [activeIndex, isLaptopOrLarger])

    const handleSceneClick = React.useCallback(
      (index: number) => {
        setActiveIndex(index)
        onSceneChange?.(index, scenes[index])
        // 同步 swiper 位置
        swiperRef.current?.slideTo(index)

        // 重置自动轮播计时器
        if (autoplay && scenes.length > 1) {
          if (intervalRef.current) {
            window.clearInterval(intervalRef.current)
          }
          intervalRef.current = window.setInterval(() => {
            setActiveIndex(prev => (prev + 1) % scenes.length)
          }, autoplayDelay)
        }
      },
      [scenes, onSceneChange, autoplay, autoplayDelay]
    )

    const handleSlideChange = React.useCallback(
      (swiper: SwiperType) => {
        // 使用 realIndex 以正确处理 loop 模式
        const index = swiper.realIndex
        setActiveIndex(index)
        onSceneChange?.(index, scenes[index])
      },
      [scenes, onSceneChange]
    )

    const activeScene = scenes[activeIndex]

    const isMediaTop = layout === 'top'

    return (
      <div
        ref={ref}
        className={cn('scene-switcher-root', 'text-info-primary w-full', className, classNames.root, {
          'aiui-dark': data.theme === 'dark',
        })}
        {...props}
      >
        {/* 标题区域 */}
        {(title || subtitle) && (
          <div className={cn('scene-switcher-header mb-6', classNames.header)}>
            {title && (
              <Heading as="h2" size={4} html={title} className={cn('scene-switcher-title', classNames.title)} />
            )}
            {subtitle && <Text as="p" size={3} className={cn('', classNames?.subtitle)} html={subtitle} />}
          </div>
        )}
        {/* Mobile/Tablet: 横向滑动卡片布局 */}
        <div className={cn('scene-switcher-swiper', 'laptop:hidden !overflow-visible', classNames.swiper)}>
          <Swiper
            modules={[FreeMode, Mousewheel, Autoplay]}
            onSwiper={swiper => {
              swiperRef.current = swiper
            }}
            className="!overflow-visible"
            onSlideChange={handleSlideChange}
            slidesPerView="auto"
            spaceBetween={12}
            freeMode={true}
            mousewheel={{ forceToAxis: true }}
            initialSlide={defaultActiveIndex}
            loop={autoplay && scenes.length > 1}
            autoplay={autoplay && scenes.length > 1 ? { delay: autoplayDelay, disableOnInteraction: false } : false}
            breakpoints={{
              768: { spaceBetween: 16 },
            }}
          >
            {scenes.map((scene, index) => (
              <SwiperSlide key={index} className="h-[462px] !w-[296px]">
                <div
                  className={cn('scene-switcher-slide', 'flex cursor-pointer flex-col', classNames.slide)}
                  onClick={() => handleSceneClick(index)}
                >
                  {/* 媒体 */}
                  <div
                    className={cn(
                      'scene-switcher-slide-media',
                      'rounded-box relative aspect-[296/320] w-full overflow-hidden',
                      classNames.slideMedia
                    )}
                  >
                    <MediaRenderer
                      media={scene.media}
                      alt={scene.title}
                      className="rounded-t-box size-full overflow-hidden object-cover"
                    />
                  </div>
                  {/* 文字内容 */}
                  <div
                    className={cn(
                      'scene-switcher-slide-content',
                      ' mt-6  border-t pt-4',
                      activeIndex === index ? 'border-info-primary border-t-4' : 'border-lines-primary',
                      classNames.slideContent
                    )}
                  >
                    <Heading
                      className={cn(
                        'scene-switcher-tab-title ',
                        activeIndex === index ? 'text-info-primary' : 'text-info-tertiary',
                        scene.title
                      )}
                      size={3}
                      as={'h3'}
                      html={scene.title}
                    />
                    {scene.description && (
                      <Text
                        className={cn(
                          'desktop:text-[16px] lg-desktop:text-[18px] line-clamp-2  text-[14px]',
                          activeIndex === index ? 'text-info-primary' : 'text-info-tertiary'
                        )}
                        html={scene.description}
                      />
                    )}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Laptop+: media-top 布局 (上图下文) */}
        {isMediaTop && (
          <div
            className={cn(
              'scene-switcher-container',
              'laptop:flex laptop:flex-col hidden',
              'laptop:h-[470px] desktop:h-[628px] lg-desktop:h-[746px] desktop:gap-[32px] gap-[16px]',
              classNames.container
            )}
          >
            {/* 媒体区域 - 显示当前选中 */}
            <div
              className={cn(
                'scene-switcher-media-wrapper',
                'rounded-box flex-1 overflow-hidden',
                classNames.mediaWrapper
              )}
            >
              {activeScene && (
                <MediaRenderer
                  media={activeScene.media}
                  alt={activeScene.title}
                  className={cn('scene-switcher-media', 'rounded-box size-full object-cover', classNames.media)}
                />
              )}
            </div>

            {/* Tab 列表 - 横向排列 */}
            <div
              className={cn(
                'scene-switcher-tab-list',
                'border-lines-primary laptop:gap-4 desktop:gap-4 lg-desktop:gap-4 flex border-t',
                classNames.tabList
              )}
            >
              {scenes.map((scene, index) => (
                <div
                  key={index}
                  ref={el => {
                    tabRefs.current[index] = el
                  }}
                  className={cn(
                    'scene-switcher-tab-item',
                    'desktop:pt-6 -mt-px flex-1 cursor-pointer border-t-2 pt-4 transition-colors',
                    activeIndex === index
                      ? 'border-info-primary border-t-4'
                      : 'hover:border-lines-primary border-transparent',
                    classNames.tabItem
                  )}
                  onClick={() => handleSceneClick(index)}
                  role="button"
                  aria-selected={activeIndex === index}
                >
                  <Heading
                    className={cn(
                      'scene-switcher-tab-title line-clamp-2',
                      activeIndex === index ? 'text-info-primary' : 'text-info-tertiary',
                      scene.title
                    )}
                    size={3}
                    as={'h3'}
                    html={scene.title}
                  />
                  {scene.description && (
                    <Text
                      className={cn(
                        'desktop:text-[16px] lg-desktop:text-[18px] line-clamp-2 text-[14px]',
                        activeIndex === index ? 'text-info-primary' : 'text-info-tertiary'
                      )}
                      html={scene.description}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Laptop+: 左文右图 / 左图右文布局 */}
        {!isMediaTop && (
          <div
            className={cn(
              'scene-switcher-container',
              'laptop:flex laptop:gap-6 desktop:gap-8 lg-desktop:gap-10 hidden',
              'laptop:h-[336px] desktop:h-[448px] lg-desktop:h-[560px]',
              layout === 'right' && 'flex-row-reverse',
              classNames.container
            )}
          >
            {/* Tab 列表 - 外层居中 */}
            <div
              className={cn(
                'scene-switcher-tab-list',
                'laptop:w-[320px] desktop:w-[400px] lg-desktop:w-[544px] flex items-center',
                classNames.tabList
              )}
            >
              {/* 内层滚动 */}
              <div className="flex max-h-full w-full flex-col overflow-y-auto">
                {scenes.map((scene, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <div className="border-lines-primary h-8 shrink-0 border-l-4" />}
                    <div
                      ref={el => {
                        tabRefs.current[index] = el
                      }}
                      className={cn(
                        'scene-switcher-tab-item',
                        'shrink-0 cursor-pointer border-l-4 pl-5',
                        activeIndex === index
                          ? 'border-info-primary border-l-[6px]'
                          : 'border-lines-primary hover:border-info-tertiary',
                        classNames.tabItem
                      )}
                      onClick={() => handleSceneClick(index)}
                      role="button"
                      aria-selected={activeIndex === index}
                    >
                      <Heading
                        className={cn(
                          'scene-switcher-tab-title desktop:line-clamp-3 line-clamp-2',
                          activeIndex === index ? 'text-info-primary' : 'text-info-tertiary',
                          scene.title
                        )}
                        size={3}
                        as={'h3'}
                        html={scene.title}
                      />
                      {scene.description && (
                        <Text
                          className={cn(
                            'desktop:text-[16px] lg-desktop:text-[18px] line-clamp-2 text-[14px]',
                            activeIndex === index ? 'text-info-primary' : 'text-info-tertiary hidden'
                          )}
                          html={scene.description}
                        />
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* 媒体区域 */}
            <div
              className={cn(
                'scene-switcher-media-wrapper',
                'rounded-box flex-1 overflow-hidden',
                classNames.mediaWrapper
              )}
            >
              {activeScene && (
                <MediaRenderer
                  media={activeScene.media}
                  alt={activeScene.title}
                  className={cn(
                    'scene-switcher-media',
                    'rounded-box size-full h-full overflow-visible object-cover',
                    classNames.media
                  )}
                />
              )}
            </div>
          </div>
        )}
      </div>
    )
  }
)

MediaSceneSwitcherV2.displayName = 'MediaSceneSwitcherV2'
export default withLayout(MediaSceneSwitcherV2)
